import { useCallback, useEffect, useMemo, useState } from "react";
import { IconButton, Stack, Popover, Card, OptionList } from "@introist/react-foundation/v2";
import { FileText } from "lucide-react";
import { Editor } from "@tiptap/react";
import { api } from "services/rpc/RpcProvider";
import { useLocalStorage } from "usehooks-ts";
import { TourCard } from "components/organisms";

export const FormLinkSelector = ({ editor }: { editor?: Editor | null }) => {
  const [popoverRef, setPopoverRef] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [hasSeenTutorial, setHasSeenTutorial] = useLocalStorage(
    "intr.htmlEditor.hasSeenFormLinkTutorial",
    false
  );
  const [showTutorial, setShowTutorial] = useState(false);

  const { data: forms } = api.forms.v2.list.useQuery({});

  // Show tutorial on first editor open
  useEffect(() => {
    if (!hasSeenTutorial && popoverRef) {
      const timer = setTimeout(() => {
        setShowTutorial(true);
        setHasSeenTutorial(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasSeenTutorial, setHasSeenTutorial, popoverRef]);

  const handleFormSelect = useCallback(
    (formId: string, formTitle: string) => {
      if (!editor) return;

      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      const formUrl = `{{form.${formId}.shareUrl}}`;

      if (hasSelection) {
        // User has selected text - convert it to a link
        editor.chain().focus().extendMarkRange("link").setLink({ href: formUrl }).run();
      } else {
        // No selection - insert link with form title as text
        editor.chain().focus().insertContent(`<a href="${formUrl}">${formTitle}</a>`).run();
      }

      setOpen(false);
    },
    [editor]
  );

  const options = useMemo(() => {
    return (forms ?? []).map(form => ({
      key: form.id,
      title: form.title,
      onClick: () => handleFormSelect(form.id, form.title)
    }));
  }, [forms, handleFormSelect]);

  const handleCloseTutorial = useCallback(() => {
    setShowTutorial(false);
  }, []);

  return (
    <>
      <Stack ref={setPopoverRef}>
        <IconButton icon={<FileText size={16} />} onClick={() => setOpen(!open)} size="large" />
      </Stack>
      <TourCard
        referenceElement={popoverRef}
        open={showTutorial}
        onClose={handleCloseTutorial}
        placement="bottom"
        title={
          <TourCard.IconTitle icon="form" color="var(--palette-primary-default)">
            Form Links
          </TourCard.IconTitle>
        }
        closeButtonText="Got it!"
      >
        <Stack vertical gap="small">
          <div>Use this button to easily insert links to forms in your messages.</div>
          <div>
            <strong>How it works:</strong>
          </div>
          <ul style={{ margin: "0 0 0 var(--spacing-medium)", padding: 0 }}>
            <li>Select text or click the button</li>
            <li>Choose a form from your workspace</li>
            <li>A clickable link will be inserted automatically</li>
          </ul>
          <div style={{ fontSize: "var(--font-size-small)", opacity: 0.8 }}>
            Perfect for adding onboarding forms, surveys, or feedback forms to your emails!
          </div>
        </Stack>
      </TourCard>
      <Popover
        referenceElement={popoverRef}
        open={open}
        onClose={() => setOpen(false)}
        closeOnContentClick={false}
      >
        <Card elevated style={{ padding: 0, minWidth: "300px" }}>
          <OptionList searchable options={options} placeholder="Search forms..." />
        </Card>
      </Popover>
    </>
  );
};
